import { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Check, Copy, X } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import {
  FOUNDERS_ONLY_COUPON_CODE,
  FOUNDERS_ONLY_EVENT_URL,
} from '@/constants/founders-only-event';
import carouselSlide1 from '@/assets/founders-only/korza-carrossel-1-abertura.png';
import carouselSlide2 from '@/assets/founders-only/korza-carrossel-2-palestrantes.png';
import carouselSlide3 from '@/assets/founders-only/korza-carrossel-3-cta.png';

const slides = [carouselSlide1, carouselSlide2, carouselSlide3];

interface FoundersOnlyPromoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FoundersOnlyPromoModal({
  open,
  onOpenChange,
}: FoundersOnlyPromoModalProps) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hasCopied, setHasCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setHasCopied(false);
    }
  }, [open]);

  useEffect(() => {
    if (!hasCopied) return;

    const timer = window.setTimeout(() => setHasCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [hasCopied]);

  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    };

    onSelect();
    carouselApi.on('select', onSelect);
    carouselApi.on('reInit', onSelect);

    return () => {
      carouselApi.off('select', onSelect);
      carouselApi.off('reInit', onSelect);
    };
  }, [carouselApi]);

  const handleCopyCoupon = useCallback(async () => {
    await navigator.clipboard.writeText(FOUNDERS_ONLY_COUPON_CODE);
    setHasCopied(true);
    toast.success('Cupom copiado!');
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden border-black p-0 sm:max-w-4xl [&>button]:hidden">
        <DialogTitle className="sr-only">Founders Only</DialogTitle>

        <div className="relative">
          <DialogClose className="absolute right-4 top-4 z-10 rounded-sm text-white opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/30">
            <X />
            <span className="sr-only">Fechar</span>
          </DialogClose>

          <div className="relative bg-black">
            <Carousel className="w-full" setApi={setCarouselApi}>
              <CarouselContent className="ml-0">
                {slides.map((slide, index) => (
                  <CarouselItem key={index} className="pl-0">
                    <img src={slide} alt="" className="w-full object-contain" />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-3 border-white/20 bg-black/50 text-white hover:bg-black/70 hover:text-white" />
              <CarouselNext className="right-3 border-white/20 bg-black/50 text-white hover:bg-black/70 hover:text-white" />
            </Carousel>

            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
              {slides.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={`Ir para slide ${index + 1}`}
                  onClick={() => carouselApi?.scrollTo(index)}
                  className={cn(
                    'h-1.5 rounded-full transition-all',
                    currentSlide === index
                      ? 'w-5 bg-white'
                      : 'w-1.5 bg-white/40'
                  )}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-4 bg-zinc-950 p-6 text-white">
            <p className="text-sm leading-relaxed text-zinc-200">
              Use o cupom da Korza para ter 15% off no seu ingresso!
            </p>

            <div
              className={cn(
                'flex h-10 w-full items-center justify-between rounded-lg border px-3 transition-colors',
                hasCopied
                  ? 'border-emerald-500 bg-emerald-500/10'
                  : 'border-zinc-700'
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                <code className="font-mono text-sm font-semibold tracking-widest">
                  {FOUNDERS_ONLY_COUPON_CODE}
                </code>
                {hasCopied ? (
                  <span className="text-xs font-medium text-emerald-400">
                    Copiado!
                  </span>
                ) : null}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'size-8 shrink-0 hover:bg-zinc-800',
                  hasCopied
                    ? 'text-emerald-400 hover:text-emerald-300'
                    : 'text-zinc-400 hover:text-white'
                )}
                aria-label={hasCopied ? 'Cupom copiado' : 'Copiar cupom'}
                onClick={handleCopyCoupon}
              >
                {hasCopied ? <Check /> : <Copy />}
              </Button>
            </div>

            <Button asChild className="h-10 w-full">
              <a
                href={FOUNDERS_ONLY_EVENT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Garantir meu ingresso
                <ArrowRight data-icon="inline-end" />
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
